import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Lotus } from "./Motifs";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/collections", label: "Collections" },
  { to: "/folk-art", label: "Folk Art" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const darkHero = location.pathname === "/" || location.pathname.startsWith("/collections/");
  const light = !scrolled && darkHero;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-700 ${
          scrolled
            ? "bg-paper/85 backdrop-blur-md border-b border-terracotta/15"
            : light
              ? "bg-gradient-to-b from-ink/50 to-transparent"
              : "bg-transparent"
        }`}
      >
        <div className="max-w-[1500px] mx-auto flex items-center justify-between px-5 md:px-10 py-4 md:py-5">
          <Link to="/" data-testid="nav-logo" className={`flex items-center gap-2.5 transition-colors ${light ? "text-paper" : "text-ink"}`}>
            <Lotus className="w-6 h-6 text-alta" />
            <span className="font-serif text-2xl tracking-[0.28em] font-medium">IPSITA</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `link-underline text-[11px] font-sans font-medium tracking-[0.22em] uppercase transition-colors ${
                    isActive
                      ? "text-alta"
                      : light
                        ? "text-paper/80 hover:text-paper"
                        : "text-ink/70 hover:text-ink"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/work-with-ipsita"
              data-testid="nav-work-cta"
              className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 text-[11px] font-sans font-medium tracking-[0.2em] uppercase transition-all duration-500 ${
                light
                  ? "border border-paper/70 text-paper hover:bg-paper hover:text-ink"
                  : "border border-ink/80 text-ink hover:bg-ink hover:text-paper"
              }`}
            >
              Work with Ipsita
            </Link>
            <button
              data-testid="nav-menu-toggle"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
              className="lg:hidden flex flex-col gap-1.5 p-2"
            >
              <span className={`w-6 h-px ${light ? "bg-paper" : "bg-ink"}`} />
              <span className={`w-6 h-px ${light ? "bg-paper" : "bg-ink"}`} />
              <span className={`w-4 h-px ${light ? "bg-paper" : "bg-ink"}`} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-[90] bg-ink text-paper flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="font-serif text-2xl tracking-[0.28em]">IPSITA</span>
              <button
                data-testid="mobile-menu-close"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="p-2 text-2xl font-light leading-none"
              >
                ×
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2" aria-label="Mobile">
              {[...LINKS, { to: "/work-with-ipsita", label: "Work with Ipsita" }].map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 28 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.12 + i * 0.07, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-serif text-4xl sm:text-5xl font-light py-2 block hover:text-alta transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <p className="px-8 pb-10 font-bengali text-paper/40 text-lg">শিল্প • ঐতিহ্য • কথা</p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
